import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Layout from '../../components/Layout';
import api from '../../api/axios';
import useAuthStore from '../../store/authStore';
import { canFiscalRelay, fiscalForward } from '../../utils/printService';
import { confirm } from '../../components/ConfirmDialog';

// Fiskal növbə: offline bağlanmış hesablar (pul kassaya yazılıb, vergi çeki kəsilməyib).
// İnternet qayıdandan sonra admin/kassir hər birini kassa aparatından keçirir.
// Ödəniş/kassa/stok YENİDƏN yazılmır — yalnız fiskal sənəd kəsilir.
const METHOD = { nagd: '💵 Nağd', kart: '💳 Kart', hesab: '🔀 Bölünmüş', delivery: '🛵 Food delivery' };
const money = (v) => `${Number(v || 0).toFixed(2)} ₼`;
const fmtDt = (v) => {
    if (!v) return '';
    const d = new Date(String(v).replace(' ', 'T'));
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleString('az-AZ', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export default function FiscalPending() {
    const { user } = useAuthStore();
    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState(null); // order id | 'all'

    const load = useCallback(() => {
        api.get('/orders/fiscal-pending')
            .then((r) => setRows(r.data.data || []))
            .catch(() => { setRows([]); toast.error('Siyahı yüklənmədi.'); });
    }, []);
    useEffect(() => { load(); }, [load]);

    // Bir hesabı kassa aparatından keçir. true = uğurlu.
    const runOne = async (row, { quiet = false } = {}) => {
        const prep = await api.post(`/orders/${row.id}/fiscal-retry-prepare`, {}, { timeout: 30000 });
        let body = {};
        if (prep.data?.relay) {
            if (!canFiscalRelay()) throw new Error('Kassa aparatı yalnız kassa kompüterindəki Fiscal POS proqramından işləyir.');
            const fwd = await fiscalForward(prep.data.relay);
            body = { fiscal_raw: fwd.raw, fiscal_status: fwd.status };
        }
        const done = await api.post(`/orders/${row.id}/fiscal-retry-complete`, body, { timeout: 30000 });
        if (!quiet) toast.success(done.data?.msg || 'Fiskal çek kəsildi.');
        return true;
    };

    const warnCard = async (row) => {
        if (!(Number(row.card_amount || 0) > 0)) return true;
        return confirm({
            title: 'Kart hissəsi var',
            message: `Bu hesabın ${money(row.card_amount)} hissəsi kartla alınıb. Kart artıq çəkilib, yalnız vergi çeki kəsiləcək. Kassa aparatı kartı yenidən istəsə, kartı TƏKRAR ÇƏKMƏYİN və əməliyyatı dayandırın.`,
            confirmText: 'Davam et',
        });
    };

    const fiscalOne = async (row) => {
        if (!(await warnCard(row))) return;
        setBusy(row.id);
        try { await runOne(row); }
        catch (err) { toast.error(err.response?.data?.msg || err.message || 'Kəsilmədi.'); }
        finally { setBusy(null); load(); }
    };

    const fiscalAll = async () => {
        const list = rows || [];
        if (!list.length) return;
        const withCard = list.filter((r) => Number(r.card_amount || 0) > 0).length;
        if (!(await confirm({
            title: 'Hamısını kassadan keçir',
            message: `${list.length} hesab sıra ilə kassa aparatından keçiriləcək.${withCard ? ` ${withCard} hesabda kart hissəsi var: aparat kartı yenidən istəsə, kartı təkrar çəkməyin.` : ''}`,
            confirmText: 'Başla',
        }))) return;
        setBusy('all');
        let ok = 0;
        for (const row of list) {
            try { await runOne(row, { quiet: true }); ok += 1; }
            catch (err) {
                toast.error(`#${row.id}: ${err.response?.data?.msg || err.message || 'kəsilmədi'}. Dayandırıldı.`, { duration: 7000 });
                break;
            }
        }
        if (ok) toast.success(`${ok} fiskal çek kəsildi.`);
        setBusy(null);
        load();
    };

    const markDone = async (row) => {
        if (!(await confirm({
            title: 'Fiskalsız siyahıdan çıxar',
            message: `#${row.id} hesabı fiskal çek kəsilmədən siyahıdan çıxarılsın? (Məs. çek əl ilə kəsilibsə.) Kassa məbləği dəyişmir.`,
            confirmText: 'Çıxar', danger: true,
        }))) return;
        setBusy(row.id);
        try {
            const r = await api.post(`/orders/${row.id}/fiscal-mark-done`);
            toast.success(r.data?.msg || 'Çıxarıldı.');
        } catch (err) { toast.error(err.response?.data?.msg || 'Alınmadı.'); }
        finally { setBusy(null); load(); }
    };

    const total = (rows || []).reduce((s, r) => s + Number(r.amount || 0), 0);

    return (
        <Layout>
            <div className="mx-auto max-w-4xl">
                <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <h1 className="text-lg font-bold text-gray-900">🧾 Fiskal gözləyənlər</h1>
                        <p className="text-xs text-gray-500">
                            İnternet olmayanda bağlanmış hesablar. Pul kassaya yazılıb, vergi çeki kəsilməyib.
                        </p>
                    </div>
                    {(rows || []).length > 0 && (
                        <button type="button" onClick={fiscalAll} disabled={!!busy}
                            className="rounded-xl bg-[#0090bb] px-4 py-2 text-sm font-bold text-white hover:bg-[#007ea4] disabled:opacity-50">
                            {busy === 'all' ? 'Keçirilir...' : `Hamısını kassadan keçir (${rows.length})`}
                        </button>
                    )}
                </div>

                {!canFiscalRelay() && (rows || []).length > 0 && (
                    <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs font-medium text-amber-800">
                        Kassa aparatı yalnız kassa kompüterindəki Fiscal POS proqramından işləyir. Bu siyahını orada açın.
                    </div>
                )}

                {rows === null ? (
                    <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-400">Yüklənir...</div>
                ) : rows.length === 0 ? (
                    <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center">
                        <div className="text-3xl">✅</div>
                        <div className="mt-2 text-sm font-semibold text-gray-700">Fiskal gözləyən hesab yoxdur</div>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                        <div className="divide-y divide-gray-100">
                            {rows.map((r) => (
                                <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-semibold text-gray-800">
                                            {r.table_name} <span className="font-mono text-xs text-gray-400">#{r.id}</span>
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {fmtDt(r.closed_at)} · {METHOD[r.method] || r.method}
                                            {r.method === 'hesab' && ` (${money(r.cash_amount)} + ${money(r.card_amount)})`}
                                        </div>
                                    </div>
                                    <div className="text-sm font-bold text-gray-900">{money(r.amount)}</div>
                                    <div className="flex gap-2">
                                        <button type="button" onClick={() => fiscalOne(r)} disabled={!!busy}
                                            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
                                            {busy === r.id ? '...' : '🧾 Kassadan keçir'}
                                        </button>
                                        {isAdmin && (
                                            <button type="button" onClick={() => markDone(r)} disabled={!!busy}
                                                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-500 hover:bg-gray-50 disabled:opacity-50">
                                                Fiskalsız çıxar
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-between border-t border-gray-100 bg-gray-50 px-4 py-2.5 text-sm">
                            <span className="text-gray-500">Cəmi ({rows.length} hesab)</span>
                            <span className="font-bold text-gray-900">{money(total)}</span>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}
